import { ArticleData } from "@/data/articles/types";
import { ArticleContentService } from "@/services/ArticleContentService";

/**
 * Loads article content from various sources and formats
 * Uses ArticleContentService as the primary loading mechanism
 */
export const loadArticleContent = async (article: ArticleData): Promise<string | undefined> => {
  console.log(`articleLoader: Loading content for article ${article.slug}`);
  return await ArticleContentService.loadContent(article);
};

/**
 * Load user behaviour sequence modelling article content
 */
async function loadUserBehaviourContent(): Promise<string | undefined> {
  console.warn('Legacy loadUserBehaviourContent function called directly. Consider using ArticleContentService instead.');
  
  // Check for production environment
  const isProduction = window.location.hostname.includes('lovable.app');
  
  try {
    // For production, try direct asset paths first
    if (isProduction) {
      console.log('PRODUCTION: Trying direct asset paths for user-behaviour');
      
      const productionPaths = [
        '/assets/InsessionadaptationviaUserbehaviourSequencemo.html',
        '/html-content/InsessionadaptationviaUserbehaviourSequencemo.html'
      ];
      
      for (const path of productionPaths) {
        try {
          const response = await fetch(path);
          if (response.ok) {
            const content = await response.text();
            console.log(`Successfully loaded HTML from production path: ${path}`);
            return content;
          }
        } catch (e) {
          console.log(`Production path failed: ${path}`);
        }
      }
    }
  
    // Regular development path
    const response = await fetch('/src/data/html-content/InsessionadaptationviaUserbehaviourSequencemo.html');
    if (response.ok) {
      const content = await response.text();
      console.log(`Successfully loaded HTML content for user-behaviour-sequence-modelling, length: ${content.length}`);
      return content;
    } else {
      console.error(`Failed to fetch HTML content: ${response.status}`);
    }
  } catch (error) {
    console.error('Error loading HTML content:', error);
  }

  // If the first attempt failed, try with a different path format
  try {
    const response = await fetch('src/data/html-content/InsessionadaptationviaUserbehaviourSequencemo.html');
    if (response.ok) {
      const content = await response.text();
      console.log(`Successfully loaded HTML content from alt path, length: ${content.length}`);
      return content;
    }
  } catch (error) {
    console.error('Error loading HTML content from alt path:', error);
  }
  
  // Return fallback content in production
  if (isProduction) {
    return getFallbackContent('user-behaviour-sequence-modelling');
  }
  
  return undefined;
}

/**
 * Provides fallback content for production when content loading fails
 */
function getFallbackContent(slug: string): string {
  return `
    <div class="p-6 bg-amber-50 border border-amber-200 rounded-lg">
      <h2 class="text-2xl font-semibold mb-3">Article Content</h2>
      <p class="mb-4">We're currently experiencing technical difficulties loading this article. 
      Please check back later or try refreshing the page.</p>
      <p class="text-sm text-gray-600">Article ID: ${slug}</p>
    </div>
  `;
}
